import { useEffect, useState } from "react";
import ClickableLink from "../../../components/utilities/ClickableLink";
import { Office365LitePermissionSelection } from "./Office365LitePermissionSelection";
import { PermissionGroupSection } from "../GoogleAuthenticationTab/PermissionGroupSection";
import {
  getOrganizationsList,
  IGroupMappings,
} from "../GoogleAuthenticationTab/GoogleAuthenticationTab";
import NotificationManager from "../../../components/notifications/NotificationManager";
import JSONWebToken from "../../../utils/JSONWebToken";
import useAsyncEffect from "../../../hooks/useAsyncEffect";
import WatsonApi from "../../../backends/WatsonApi";
import useSWR from "swr";
import SchoolBlocksLoadingIndicator from "../../../components/utilities/SchoolBlocksLoadingIndicator";
import styles from "../GoogleAuthenticationTab/AuthenticationTab.module.scss";
import { getConfig } from "../../../_configs/AppConfig";
import { useStores } from "../../../stores/useStores";
import { runInAction } from "mobx";

const { publicRuntimeConfig } = getConfig();

function Office365LiteTab(props: { currentIdP?: "google" | "office365" }) {
  return (
    <div>
      <ul>
        <li>
          {props.currentIdP === "office365" ? (
            "We are syncing your staff directory and permissions with Office 365."
          ) : (
            <>
              We are <strong>not</strong> syncing your staff directory and
              permissions with Office 365 at this time. Please contact support
              if you would like to change this.
            </>
          )}
        </li>
      </ul>
    </div>
  );
}

function Office365MediumTab(props: {
  currentIdP?: "google" | "office365";
  identityGroupsList: IGroupMappings;
}) {
  const { organizationStore, interfaceStore } = useStores();
  const [officeAuthenticateRoute, setOfficeAuthenticateRoute] =
    useState<string>("");
  useAsyncEffect(async () => {
    const jwt = await new JSONWebToken().encode(
      organizationStore.organization.id,
      interfaceStore.currentFullUrl,
    );
    setOfficeAuthenticateRoute(
      `https://api.${publicRuntimeConfig.APP_ENV}.cleversite.com/authentication/microsoft-graph-admin/?state=${jwt}`,
    );
  }, []);

  return (
    <div>
      {props.identityGroupsList ? (
        <div>
          <ul>
            <li>
              Congratulations! Your Office365Tab domain is all setup to work
              with School Blocks. If there are any permission issues with
              Office365Tab and your domain, feel free to update our application
              in your Office365Tab Active Directory instance by{" "}
              <a href={officeAuthenticateRoute}>clicking on this link</a> and
              logging in with a domain administration account.
            </li>
            <li>
              {props.currentIdP === "office365" ? (
                <span>
                  We are syncing your staff directory and permissions with
                  Office 365.
                </span>
              ) : (
                <>
                  We are <strong>not</strong> syncing your staff directory and
                  permissions with Office 365 at this time. Please contact
                  support if you would like to change this.
                </>
              )}
            </li>
          </ul>
        </div>
      ) : (
        <div>
          <p>
            After you have entered the authentication domain and clicked
            &#34;save&#34; at the bottom of this form, click the below button to
            login and add this application to your organization.
          </p>
          <ClickableLink type="button" href={officeAuthenticateRoute}>
            <div
              className="sb-login-button sb-background-color-office365"
              style={{ width: "15em" }}
            >
              <span className="sb-office365-icons sb-office365-logo" />
              <br />
              Setup School Blocks with Office 365 for Education
            </div>
          </ClickableLink>
        </div>
      )}
    </div>
  );
}

const PermissionSelections = {
  LitePermissions: Office365LitePermissionSelection,
  MediumPermissions: PermissionGroupSection,
};

interface IOffice365PropsUnknownGroupSearch {
  currentIdP: "google" | "office365";
}

interface IOffice365PropsWithGroupSearch extends IOffice365PropsUnknownGroupSearch {
  currentIdP: "google" | "office365";
}

export function Office365Tab(props: IOffice365PropsWithGroupSearch) {
  const { organizationStore } = useStores();
  const {
    domain,
    userFieldsToOverrideAndSync,
    hasLitePermissions,
    hasMediumPermissions,
  } = organizationStore.organization.json_data.settings.identity.office365;

  const [hasChanged, setHasChanged] = useState(false);
  const [authenticationDomain, setAuthenticationDomain] = useState(domain);
  const [syncTitle, setSyncTitle] = useState(
    userFieldsToOverrideAndSync?.title,
  );
  const [syncPhone, setSyncPhone] = useState(
    userFieldsToOverrideAndSync?.phone,
  );
  const [syncPhoto, setSyncPhoto] = useState(
    userFieldsToOverrideAndSync?.photo,
  );
  const [syncEmail, setSyncEmail] = useState(
    userFieldsToOverrideAndSync?.email,
  );

  const initialGroupMappings =
    organizationStore?.organization?.json_data?.settings?.identity?.office365
      ?.groupMappings;
  const [groupMappings, setGroupMappings] = useState(initialGroupMappings);
  const { data, isLoading } = useSWR(
    "fetch-group-mappings-office",
    async () => {
      try {
        const client = await WatsonApi();
        const response =
          await client.apis.settings.settings_directory_group_mappings({
            id: organizationStore.currentOrganization.id,
            selected_provider: "office365",
          });
        return JSON.parse(response.data);
      } catch (e) {
        console.error(e);
        return {
          message: "Error fetching auth group data.",
        };
      }
    },
    {
      revalidateOnMount: true,
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );
  useEffect(() => {
    if (!isLoading) {
      setGroupMappings(() => {
        const value = {};
        getOrganizationsList(data).forEach((org) => {
          const obj = {
            adminGroup: initialGroupMappings?.[org.id]?.adminGroup || [],
            staffGroup: initialGroupMappings?.[org.id]?.staffGroup || [],
            department: [],
            companyName: "",
            teacherGroup: [],
            studentGroup: [],
          };

          if (hasLitePermissions) {
            obj.department = initialGroupMappings?.[org.id]?.department || "";
          }

          if (org.type === "district" && hasLitePermissions) {
            obj.companyName =
              groupMappings?.[data.district?.id]?.companyName || "";
          }

          if (org.type !== "district") {
            obj.teacherGroup =
              initialGroupMappings?.[org.id]?.teacherGroup || [];
            obj.studentGroup =
              initialGroupMappings?.[org.id]?.studentGroup || [];
          }

          value[org.id] = obj;
        });
        return value;
      });
    }
  }, [data, isLoading]);

  async function handleSubmit(e) {
    e.preventDefault();

    try {
      const client = await WatsonApi();
      const response = await client.apis.settings.settings_update(
        {
          id: organizationStore.currentOrganization.id,
        },
        {
          requestBody: {
            identity: {
              provider: "office365",
              office365: {
                hasLitePermissions: hasLitePermissions,
                hasMediumPermissions: hasMediumPermissions,
                domain: authenticationDomain,
                userFieldsToOverrideAndSync: {
                  title: syncTitle,
                  phone: syncPhone,
                  photo: syncPhoto,
                  email: syncEmail,
                },
                groupMappings: groupMappings,
              },
            },
          },
        },
      );

      const settings = response.body.settings;
      if (settings) {
        runInAction(() => {
          organizationStore.organization.json_data.settings = settings;
        });
        NotificationManager.success("Settings successfully updated!");
      }
    } catch (e) {
      switch (e.statusCode) {
        case 400:
          NotificationManager.error("Bad Request: Settings not updated.");
          break;
        case 404:
          NotificationManager.error(
            "Not Found: Organization settings not found.",
          );
          break;
        case 500:
          NotificationManager.error(
            "Internal Server Error: Settings not updated.",
          );
          break;
        default:
          NotificationManager.error("Unknown Error: Settings not updated.");
          break;
      }
    }
  }

  const PermissionComponent =
    PermissionSelections[
      hasLitePermissions ? "LitePermissions" : "MediumPermissions"
    ];

  return isLoading ? (
    <div style={{ textAlign: "center", marginTop: "6em" }}>
      <SchoolBlocksLoadingIndicator fill={"$dark-gray"} />
    </div>
  ) : (
    <div>
      <div className="sb-settings-block-content-heading">
        <h4>Allow your users to authenticate with Office 365 for Education.</h4>
      </div>
      <form
        id="district-update-form"
        onSubmit={handleSubmit}
        onChange={() => setHasChanged(true)}
      >
        <div style={{ marginBottom: "2em" }}>
          <label>Authentication Domain:</label>
          <input
            title="Office365Tab authentication domain"
            width="50"
            type="text"
            placeholder="Authentication Domain"
            value={authenticationDomain}
            onChange={(e) => setAuthenticationDomain(e.target.value)}
          />
        </div>
        {!isLoading && data && hasLitePermissions ? (
          <Office365LiteTab currentIdP={props.currentIdP} />
        ) : (
          <Office365MediumTab
            currentIdP={props.currentIdP}
            identityGroupsList={data.identityGroupsList}
          />
        )}
        {!isLoading &&
          data &&
          (hasLitePermissions || data?.identityGroupsList) && (
            <div>
              <div>
                <label>Staff Directories:</label>
                <p>
                  Users listed in the groups identified below will be synced to
                  SchoolBlocks so that they show up in Staff Directory pages and
                  blocks. Users are able to edit their displayed information
                  such as first name, last name, email, etc. If you would like
                  to override any changes made by the user and sync specific
                  fields, check the boxes below. Not all fields are available to
                  overwrite and sync. In order to use photo syncing you will
                  need to re-authenticate Office365 with Schoolblocks
                </p>
              </div>
              <div>
                Fields to sync from the Office365 user object for each staff
                member:
                <br />
                {organizationStore.organization.json_data.settings.identity
                  .office365.userFieldsToOverrideAndSync && (
                  <div>
                    <div
                      className={styles.tooltip}
                      style={{ display: "inline-block" }}
                      data-content={
                        "When enabled, individual staff members are unable to edit titles within SchoolBlocks. Titles must be updated in Microsoft Directory."
                      }
                    >
                      <input
                        type={"checkbox"}
                        checked={syncTitle}
                        onChange={() => setSyncTitle((v) => !v)}
                        style={{ marginLeft: "1em", width: "0.8em" }}
                        title={"Sync a staff member's title"}
                        id={
                          "UpdateForm[identity][office365][userFieldsToOverrideAndSync][title]"
                        }
                      />{" "}
                      <label
                        htmlFor={
                          "UpdateForm[identity][office365][userFieldsToOverrideAndSync][title]"
                        }
                      >
                        Title
                      </label>
                    </div>
                    <div>
                      <input
                        type={"checkbox"}
                        checked={syncPhone}
                        onChange={() => setSyncPhone((v) => !v)}
                        style={{ marginLeft: "1em", width: "0.8em" }}
                        title={"Sync a staff member's phone"}
                        id={
                          "UpdateForm[identity][office365][userFieldsToOverrideAndSync][phone]"
                        }
                      />{" "}
                      <label
                        htmlFor={
                          "UpdateForm[identity][office365][userFieldsToOverrideAndSync][phone]"
                        }
                      >
                        Phone
                      </label>
                    </div>
                    <div
                      className={styles.tooltip}
                      style={{ display: "inline-block" }}
                      data-content={"Requires re-authentication"}
                    >
                      <input
                        type={"checkbox"}
                        checked={syncPhoto}
                        onChange={() => setSyncPhoto((v) => !v)}
                        style={{ marginLeft: "1em", width: "0.8em" }}
                        title={"Sync a staff member's photo"}
                        id={
                          "UpdateForm[identity][office365][userFieldsToOverrideAndSync][photo]"
                        }
                      />{" "}
                      <label
                        htmlFor={
                          "UpdateForm[identity][office365][userFieldsToOverrideAndSync][photo]"
                        }
                      >
                        Photo
                      </label>
                    </div>
                    <div>
                      <input
                        type={"checkbox"}
                        checked={syncEmail}
                        onChange={() => setSyncEmail((v) => !v)}
                        style={{ marginLeft: "1em", width: "0.8em" }}
                        title={"Sync a staff member's email"}
                        id={
                          "UpdateForm[identity][office365][userFieldsToOverrideAndSync][email]"
                        }
                      />{" "}
                      <label
                        htmlFor={
                          "UpdateForm[identity][office365][userFieldsToOverrideAndSync][email]"
                        }
                      >
                        Email
                      </label>
                    </div>
                  </div>
                )}
              </div>
              {!isLoading && data.district && hasLitePermissions && (
                <div style={{ marginBottom: "2em" }}>
                  <label>Company Name:</label>
                  <input
                    type="text"
                    onChange={(e) => {
                      setGroupMappings((prev) => ({
                        ...prev,
                        [data.district.id]: {
                          ...prev[data.district.id],
                          companyName: e.target.value,
                        },
                      }));
                    }}
                    value={groupMappings[data.district.id].companyName}
                  />
                </div>
              )}
              <div>
                <table
                  className="table table-striped"
                  // fixed layout so badge content can't reflow the column widths
                  style={{ tableLayout: "fixed", width: "100%" }}
                >
                  <thead>
                    <tr>
                      <th style={{ textAlign: "center" }}>Organization</th>
                      <th style={{ textAlign: "center" }}>Permission Groups</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!isLoading &&
                      data &&
                      data.district &&
                      getOrganizationsList(data).map((org) => (
                        <PermissionComponent
                          organization={org}
                          useGroupSearch={data.useGroupSearch}
                          identityGroupsList={data.identityGroupsList}
                          key={org.id}
                          groupOuReference={data.groupOuReference}
                          provider={"office365"}
                          groupMappings={groupMappings}
                          setGroupMappings={setGroupMappings}
                          setHasChanged={setHasChanged}
                        />
                      ))}
                  </tbody>
                </table>
              </div>
              <div className="sb-settings-block-footer">
                <button
                  type="submit"
                  className="btn sb-organization-color-element-bg"
                  name="submit"
                  disabled={!hasChanged}
                >
                  Save
                </button>
              </div>
            </div>
          )}
      </form>
    </div>
  );
}
